import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { MdExpandMore, MdSearch, MdClose } from 'react-icons/md';
import { cn } from '../../lib/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  /** Shown as the first “clear” row. Pass empty string / omit with showClearOption=false to hide. */
  allLabel?: string;
  showClearOption?: boolean;
  searchPlaceholder?: string;
  className?: string;
  buttonClassName?: string;
  disabled?: boolean;
  createLabel?: string;
  onCreate?: (query: string) => void;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  allLabel = 'All',
  showClearOption = true,
  searchPlaceholder = 'Search...',
  className,
  buttonClassName,
  disabled = false,
  createLabel = 'Add new',
  onCreate,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.value.toLowerCase().includes(q) ||
        (o.sublabel || '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const menuMaxH = 288; // max-h-72
    const spaceBelow = viewportH - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    const openUpward = spaceBelow < Math.min(menuMaxH, 200) && spaceAbove > spaceBelow;
    const maxHeight = Math.max(160, Math.min(menuMaxH, openUpward ? spaceAbove : spaceBelow));
    const width = Math.max(rect.width, 220);
    let left = rect.left;
    if (left + width > viewportW - 8) left = Math.max(8, viewportW - width - 8);

    setMenuStyle({
      position: 'fixed',
      zIndex: 9999,
      left,
      width,
      maxHeight,
      ...(openUpward
        ? { bottom: viewportH - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener('resize', onScrollOrResize);
    // capture scroll from any scrollable ancestor
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
      setQuery('');
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const displayLabel = selected?.label || (value ? value : placeholder);

  const commitCreateOrSelect = () => {
    const q = query.trim();
    if (!q) return;
    const exact = options.find(
      (o) => o.label.toLowerCase() === q.toLowerCase() || o.value.toLowerCase() === q.toLowerCase(),
    );
    if (exact) {
      onChange(exact.value);
      setOpen(false);
      setQuery('');
      return;
    }
    if (!onCreate) return;
    onCreate(q);
    setOpen(false);
    setQuery('');
  };

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="overflow-hidden flex flex-col rounded-xl border border-[hsl(var(--border))] dark:border-white/15 bg-[hsl(var(--card))] dark:bg-[#0f172a] text-[hsl(var(--foreground))] dark:text-white shadow-2xl"
        >
          <div className="p-2 border-b border-[hsl(var(--border))] dark:border-white/10 flex items-center gap-2 shrink-0">
            <MdSearch className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                e.stopPropagation();
                commitCreateOrSelect();
              }}
              placeholder={searchPlaceholder}
              className="flex-1 text-sm outline-none bg-transparent text-[hsl(var(--foreground))] dark:text-white placeholder:text-[hsl(var(--muted-foreground))]"
              autoFocus
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              >
                <MdClose className="w-4 h-4" />
              </button>
            )}
          </div>
          <ul className="overflow-y-auto flex-1 py-1 overscroll-contain">
            {showClearOption && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    onChange('');
                    setOpen(false);
                    setQuery('');
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm hover:bg-[#0088cc]/10 dark:hover:bg-[#0088cc]/20',
                    !value && 'bg-[#0088cc]/10 dark:bg-[#0088cc]/20 text-[#0088cc] font-semibold',
                  )}
                >
                  {allLabel || placeholder}
                </button>
              </li>
            )}
            {filtered.length === 0 ? (
              <li className="px-3 py-4 text-sm text-[hsl(var(--muted-foreground))] text-center">
                No matches
              </li>
            ) : (
              filtered.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={cn(
                      'w-full text-left px-3 py-2 text-sm hover:bg-[#0088cc]/10 dark:hover:bg-[#0088cc]/20 text-[hsl(var(--foreground))] dark:text-white',
                      value === opt.value && 'bg-[#0088cc]/10 dark:bg-[#0088cc]/20 text-[#0088cc] font-semibold',
                    )}
                  >
                    <div className="truncate">{opt.label}</div>
                    {opt.sublabel && (
                      <div className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">
                        {opt.sublabel}
                      </div>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
          {onCreate && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={commitCreateOrSelect}
              disabled={!query.trim()}
              className="shrink-0 w-full text-left px-3 py-2.5 text-sm font-semibold text-[#0088cc] border-t border-[hsl(var(--border))] dark:border-white/10 bg-[hsl(var(--card))] dark:bg-[#0f172a] hover:bg-[#0088cc]/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {query.trim() ? `+ Add “${query.trim()}”` : `+ ${createLabel} (type a name, then Enter)`}
            </button>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center justify-between gap-2 py-2.5 px-3 rounded-xl border border-[#0088cc]/20 text-sm font-medium bg-white dark:bg-white/[0.05] dark:border-white/15 dark:text-white text-left',
          'hover:border-[#0088cc]/40 focus:outline-none focus:ring-2 focus:ring-[#0088cc]/20',
          disabled && 'opacity-50 cursor-not-allowed',
          !value && 'text-[#0088cc]/65 dark:text-white/40',
          buttonClassName,
        )}
        aria-expanded={open}
      >
        <span className="truncate">{displayLabel}</span>
        <MdExpandMore
          className={cn('w-5 h-5 shrink-0 text-[#0088cc]/65 dark:text-white/50', open && 'rotate-180')}
        />
      </button>
      {menu}
    </div>
  );
}
